/**
 * Client for the Payload CMS (greenfilter-cms) that blog content is being
 * migrated to. Kept separate from lib/api.ts (the product catalog API) since
 * this points at a different backend with its own response shape.
 */

export const CMS_API_URL = process.env.CMS_API_URL || 'http://localhost:3001';

// CMS_API_URL has no NEXT_PUBLIC_ prefix, so it doesn't exist in the browser
// bundle (same pitfall the comments elsewhere in this file already warn
// about for media URLs). The lead/comment functions below run from client
// components ('use client' - the popup and comment form), so they need this
// public counterpart instead; everything else in this file runs server-side
// and keeps using CMS_API_URL.
const PUBLIC_CMS_API_URL = process.env.NEXT_PUBLIC_CMS_API_URL || CMS_API_URL;

export interface CmsMediaSize {
  url: string;
  width: number;
  height: number;
}

export interface CmsMedia {
  id: number;
  alt: string;
  url: string;
  width: number;
  height: number;
  sizes?: Record<string, CmsMediaSize>;
}

export interface CmsRichTextBlock {
  blockType: 'richText';
  id: string;
  content: { root: CmsLexicalNode };
}

export interface CmsTableBlock {
  blockType: 'table';
  id: string;
  heading?: string;
  headers: { label: string }[];
  rows: { cells: { value: string }[] }[];
}

export type CmsContentBlock = CmsRichTextBlock | CmsTableBlock;

export interface CmsLexicalNode {
  type: string;
  tag?: string;
  text?: string;
  format?: string | number;
  children?: CmsLexicalNode[];
  [key: string]: unknown;
}

export interface CmsFaq {
  question: string;
  answer: string;
}

export interface CmsPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  category?: { title: string };
  readTime?: string;
  publishedDate: string;
  author?: string;
  heroImage: CmsMedia;
  content: CmsContentBlock[];
  faqs?: CmsFaq[];
  metaTitle?: string;
  metaDescription?: string;
  keywords?: { keyword: string }[];
}

/** Absolute URL for a CMS-hosted media file (media URLs from the API are host-relative). */
export function cmsMediaUrl(url: string): string {
  return `${CMS_API_URL}${url}`;
}

export async function getCmsPosts(revalidateSeconds = 60): Promise<CmsPost[]> {
  const params = new URLSearchParams({
    sort: '-publishedDate',
    depth: '1',
    limit: '100',
  });

  try {
    const res = await fetch(`${CMS_API_URL}/api/posts?${params.toString()}`, {
      next: { revalidate: revalidateSeconds },
    });

    if (!res.ok) return [];

    const data = await res.json();
    const docs: CmsPost[] = data?.docs ?? [];
    // The CMS can contain incomplete drafts (e.g. a post created but never
    // filled in) - exclude anything missing the fields every consumer of
    // this list assumes are present, so one bad draft can't break the
    // static build or the preview list/detail pages.
    return docs.filter((post) => post.slug && post.title && post.heroImage);
  } catch (error) {
    console.error('Failed to fetch CMS posts:', error);
    return [];
  }
}

export async function getCmsPostBySlug(slug: string, revalidateSeconds = 60): Promise<CmsPost | null> {
  const params = new URLSearchParams({
    'where[slug][equals]': slug,
    depth: '2',
    limit: '1',
  });

  try {
    const res = await fetch(`${CMS_API_URL}/api/posts?${params.toString()}`, {
      // ISR: served from cache, revalidated in the background at most this
      // often - avoids a live CMS round-trip on every request while still
      // picking up edits shortly after they're published.
      next: { revalidate: revalidateSeconds },
    });

    if (!res.ok) return null;

    const data = await res.json();
    return data?.docs?.[0] ?? null;
  } catch (error) {
    console.error('Failed to fetch CMS post:', error);
    return null;
  }
}

export interface CmsProductType {
  id: number;
  name: string;
}

export interface CmsProduct {
  id: number;
  name: string;
  gfuPartNum: string;
  slug: string;
  productType?: CmsProductType | null;
  mainImageUrl?: string | null;
  buyUrl?: string | null;
  _status?: 'draft' | 'published';
  /** Care products (cleaners, recharge oil) linked to this filter. Unpublished ones come back as bare ids. */
  relatedProducts?: (number | CmsProduct)[] | null;
}

export interface CmsFitment {
  id: number;
  yearStart: number;
  yearEnd: number;
  engine?: string | null;
  fuelSystem?: string | null;
  trim?: string | null;
  vehicleModel: { id: number; name: string; make: { id: number; name: string } };
  product: CmsProduct | null;
}

export interface CmsVariantPrice {
  product: number;
  price: number;
  compareAtPrice?: number | null;
}

/**
 * Every fitment of one make whose model name contains `modelName`
 * (e.g. Chevrolet + "corvette" -> Corvette, Corvette Z06, Corvette ZR1), with
 * the product embedded. Public reads only return fitments of published products.
 */
export async function getCmsFitmentsForModel(
  makeName: string,
  modelName: string,
  revalidateSeconds = 3600,
): Promise<CmsFitment[]> {
  const params = new URLSearchParams({
    'where[vehicleModel.make.name][equals]': makeName,
    'where[vehicleModel.name][like]': modelName,
    sort: 'yearStart',
    depth: '2',
    limit: '500',
  });

  try {
    const res = await fetch(`${CMS_API_URL}/api/fitments?${params.toString()}`, {
      next: { revalidate: revalidateSeconds },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const docs: CmsFitment[] = data?.docs ?? [];
    return docs.filter((f) => f.product?.gfuPartNum && f.vehicleModel?.name);
  } catch (error) {
    console.error('Failed to fetch CMS fitments:', error);
    return [];
  }
}

/** Active variant prices for the given products (used when the Shopify buy button can't load). */
export async function getCmsVariantPrices(productIds: number[], revalidateSeconds = 3600): Promise<CmsVariantPrice[]> {
  if (!productIds.length) return [];
  const params = new URLSearchParams({
    'where[product][in]': productIds.join(','),
    depth: '0',
    limit: '200',
  });

  try {
    const res = await fetch(`${CMS_API_URL}/api/product-variants?${params.toString()}`, {
      next: { revalidate: revalidateSeconds },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.docs ?? [];
  } catch (error) {
    console.error('Failed to fetch CMS variant prices:', error);
    return [];
  }
}

export interface CmsLeadInput {
  name?: string;
  email: string;
  phone?: string;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleEngine?: string;
  postSlug: string;
}

interface CmsSubmitResponse {
  success: boolean;
  message: string;
  errors?: Record<string, unknown>;
}

/** Submits the blog lead-capture popup form directly to the CMS's `leads` collection. */
export async function postCmsLead(input: CmsLeadInput): Promise<CmsSubmitResponse> {
  try {
    const res = await fetch(`${PUBLIC_CMS_API_URL}/api/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, message: data?.errors?.[0]?.message || 'Something went wrong. Please try again.' };
    return { success: true, message: data?.message || 'Submitted' };
  } catch (error) {
    console.error('Failed to submit CMS lead:', error);
    return { success: false, message: 'Something went wrong. Please try again.' };
  }
}

export interface CmsApprovedComment {
  id: number;
  authorName: string;
  commentBody: string;
  createdAt: string;
}

/** Approved, publicly-visible comments for a post - the CMS's `comments` access control already excludes pending ones and `authorEmail` for unauthenticated requests. */
export async function getCmsComments(postId: number, revalidateSeconds = 30): Promise<CmsApprovedComment[]> {
  const params = new URLSearchParams({
    'where[post][equals]': String(postId),
    'where[approved][equals]': 'true',
    sort: '-createdAt',
    depth: '0',
    limit: '100',
  });

  try {
    const res = await fetch(`${PUBLIC_CMS_API_URL}/api/comments?${params.toString()}`, {
      next: { revalidate: revalidateSeconds },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.docs ?? [];
  } catch (error) {
    console.error('Failed to fetch CMS comments:', error);
    return [];
  }
}

interface CmsCommentInput {
  post: number;
  authorName: string;
  authorEmail: string;
  commentBody: string;
  recaptchaToken?: string;
}

/** Submits a new blog comment to the CMS - always lands as pending (`approved: false`) until moderated in the Payload admin. */
export async function postCmsComment(input: CmsCommentInput): Promise<CmsSubmitResponse> {
  try {
    const res = await fetch(`${PUBLIC_CMS_API_URL}/api/comments/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, message: data?.error || 'Something went wrong. Please try again.' };
    return { success: true, message: data?.message || 'Comment submitted for review' };
  } catch (error) {
    console.error('Failed to submit CMS comment:', error);
    return { success: false, message: 'Something went wrong. Please try again.' };
  }
}
